import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../../components/LanguageSelector';

const LEGAL_LINKS = [
    { href: 'https://sporttia.com/terms', labelKey: 'contact.legalWarning' },
    { href: 'https://sporttia.com/privacy', labelKey: 'contact.privacyPolicy' },
    { href: 'https://sporttia.com/cookies', labelKey: 'contact.cookiesPolicy' },
    {
        href: 'https://sporttia.com/contracting-conditions',
        labelKey: 'contact.contractingConditions',
    },
] as const;

/**
 * Campo de las pantallas de acceso: isla clara sobre la banda oscura, con el
 * icono dentro y sin etiqueta flotante, igual que en sporttia-play.
 *
 * Los colores salen de los tokens `auth-field-*` y no de `--card` porque
 * estos formularios se ven siempre sobre el mismo negro, mande lo que mande
 * el modo del navegador: siguiendo a `--card` quedarían negros sobre negro.
 */
export const authFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: 56,
        borderRadius: '0.75rem',
        backgroundColor: 'var(--auth-field)',
        color: 'var(--auth-field-foreground)',
        '& fieldset': { border: 'none' },
    },
    '& .MuiOutlinedInput-input::placeholder': {
        color: 'var(--auth-field-muted)',
        opacity: 1,
    },
};

/**
 * Botón principal de las pantallas de acceso.
 *
 * MUI pinta el estado desactivado con su gris de tema, que sobre la banda
 * oscura se convierte en un rectángulo casi negro con letra casi negra: el
 * botón desaparece y no se distingue de un hueco. Aquí el desactivado
 * mantiene el lima con la tinta encima y baja la opacidad, que es como lo
 * marca el sistema de diseño.
 */
export const authButtonSx = {
    '&.Mui-disabled': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        opacity: 0.5,
    },
};

/**
 * Logotipo con el distintivo de centros debajo. Corona todas las pantallas de
 * acceso, en el sitio y el tamaño que ocupa en sporttia-play.
 *
 * El distintivo es lo único que separa esta puerta de la del jugador, así que
 * tiene que estar donde se mira antes de escribir nada. Va en lima de relleno
 * con la tinta encima (13,9:1), no en lima como letra, que sobre negro
 * perdería el peso de sello.
 */
export function AuthBrandHeader() {
    const { t } = useTranslation();

    return (
        <Box className="mb-8 flex flex-col items-center gap-3">
            <img
                src="/images/logo-horizontal-sporttia.svg"
                alt="Sporttia"
                className="h-10 w-auto object-contain sm:h-12"
            />
            <span className="bg-brand text-primary-foreground rounded-md px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase">
                {t('login.managerBadge')}
            </span>
        </Box>
    );
}

type AuthScreenProps = {
    children: React.ReactNode;
    /**
     * Ancho del contenido. `md` es la medida de un formulario suelto
     * (recuperar contraseña); `5xl` la de la pantalla de entrar, que añade al
     * lado una columna de presentación.
     */
    width?: 'md' | '5xl';
};

/**
 * Carcasa de las pantallas de acceso: la banda de marca, el selector de
 * idioma y el pie legal.
 *
 * Vive aparte porque entrar y recuperar contraseña están a un clic la una de
 * la otra: si cada una se pintara su propio fondo, la que se tocase menos se
 * quedaría atrás y el salto se vería al pulsar el enlace.
 */
export default function AuthScreen({
    children,
    width = 'md',
}: AuthScreenProps) {
    const { t } = useTranslation();

    return (
        <Box className="bg-auth text-auth-foreground flex min-h-svh flex-1 flex-col">
            {/* La barra superior solo lleva el idioma: el logotipo va sobre el
                formulario, como en sporttia-play. */}
            <Box className="flex items-center justify-end px-4 py-4 sm:px-6">
                <Box className="[&_.MuiChip-root]:text-auth-foreground [&_.MuiChip-root]:border-auth-foreground/40">
                    <LanguageSelector />
                </Box>
            </Box>

            <Box
                component="main"
                className="flex flex-1 items-center justify-center px-4 py-6 sm:px-6"
            >
                <Box
                    className={`w-full ${width === '5xl' ? 'max-w-5xl' : 'max-w-md'}`}
                >
                    {children}
                </Box>
            </Box>

            <Box component="footer" className="px-4 py-6 text-center sm:px-6">
                <p className="text-auth-muted text-sm">
                    © {new Date().getFullYear()} Sporttia
                </p>
                <ul className="mt-2 flex list-none flex-wrap justify-center gap-x-4 gap-y-1 p-0 text-xs">
                    {LEGAL_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-auth-muted hover:text-auth-foreground no-underline transition-colors hover:underline"
                            >
                                {t(link.labelKey)}
                            </a>
                        </li>
                    ))}
                </ul>
            </Box>
        </Box>
    );
}
